<template>
  <div class="full-width">
    <expansion-panel
      key="expansion-panel-complements"
      uid="services-panel"
      :title="t('offer-manager.create.step-services.title')"
      :readonly="panelReadonly"
      :hideDivider="panelHideDivider"
      :safePaddingBottom="safePaddingBottom">
      <template v-slot:panel-content>
        <m-form-row>
          <div class="d-flex justify-start flex-wrap">
            <service-checkbox
              class="mr-8 my-2"
              data-cy="service-check-honda-plus-go"
              :error="''"
              :isSelectable="true"
              variant="honda-plus-go"
              :checkValue="servicesProducts.includes(Product.HondaPlusGo)"
              @changeCheckValue="() => updateOffer('setServicesProduct', Product.HondaPlusGo)"
              @showInfo="showInfo"
              :disabled="
                !vehicleModelName || !vehicleModelSubname || !canHaveHPGo || !isHondaPlusSupported || disabled
              " />
            <service-checkbox
              class="mr-8 my-2"
              data-cy="service-check-honda-plus"
              :error="''"
              :isSelectable="true"
              variant="honda-plus"
              :checkValue="servicesProducts.includes(Product.HondaPlus)"
              @changeCheckValue="() => updateOffer('setServicesProduct', Product.HondaPlus)"
              @showInfo="showInfo"
              :disabled="
                !canHaveHP || !vehicleModelName || !vehicleModelSubname || !isHondaPlusSupported || disabled
              " />
            <service-checkbox
              class="mr-8 my-2"
              data-cy="service-check-honda-mapit"
              :error="''"
              :isSelectable="true"
              variant="honda-mapit"
              :checkValue="servicesProducts.includes(Product.HondaMapit)"
              @changeCheckValue="() => updateOffer('setServicesProduct', Product.HondaMapit)"
              @showInfo="showInfo"
              :disabled="!vehicleModelName || !vehicleModelSubname || disabled" />
            <service-checkbox
              class="mr-8 my-2"
              data-cy="service-check-honda-seguros"
              :error="''"
              :isSelectable="vehicleModelInsurance"
              variant="honda-seguro-normal"
              :checkValue="servicesProducts.includes(Product.Insurance)"
              @changeCheckValue="() => updateOffer('setServicesProduct', Product.Insurance)"
              @showInfo="showInfo"
              :disabled="
                (!servicesProducts.includes(Product.HondaPlus) && !servicesProducts.includes(Product.HondaPlusGo)) ||
                !vehicleModelName ||
                !vehicleModelSubname ||
                !vehicleModelInsurance ||
                disabled
              " />
            <service-checkbox
              class="my-2"
              data-cy="service-check-honda-finance"
              :error="''"
              :isSelectable="true"
              variant="honda-finance"
              :checkValue="servicesProducts.includes(Product.Finance)"
              @changeCheckValue="() => updateOffer('setServicesProduct', Product.Finance)"
              :disabled="
                (!servicesProducts.includes(Product.HondaPlus) && !servicesProducts.includes(Product.HondaPlusGo)) ||
                !vehicleModelName ||
                !vehicleModelSubname ||
                !availableFinancialServicesOption ||
                disabled
              " />
          </div>
        </m-form-row>

        <m-form-row v-if="servicesProducts.includes(Product.HondaPlus)">
          <template v-slot:custom-label>
            <div class="d-flex align-center text-left">
              <span>{{ t('offer-manager.create.step-services.lbl-hp') }}</span>
              <div @click="showInfo('honda-plus')">
                <v-icon dense class="fa-light fa-circle-info ml-1 px-2 click" color="tertiary" />
              </div>
            </div>
          </template>
          <template v-slot:default>
            <mapit-input
              uid="offerServicesHondaPlusPriceRate"
              :value="servicesHondaPlusPrice.toFixed(2)"
              :placeholder="t('offer-manager.create.step-services.lbl-rate-services-placeholder')"
              :type="InputTypes.PRICE"
              :forceFloat="true"
              :isReadonly="true"
              @validateForm="validateForm"
              :disabled="disabled" />
          </template>
        </m-form-row>
        <m-form-row v-if="servicesProducts.includes(Product.HondaPlusGo)">
          <template v-slot:custom-label>
            <div class="d-flex align-center text-left">
              <span>{{ t('offer-manager.create.step-services.lbl-hpg') }}</span>
              <div @click="showInfo('honda-plus-go')">
                <v-icon dense class="fa-light fa-circle-info ml-1 px-2 click" color="tertiary" />
              </div>
            </div>
          </template>
          <template v-slot:default>
            <mapit-input
              uid="offerServicesHondaPlusGoPriceRate"
              :value="servicesHondaPlusPrice.toFixed(2)"
              :placeholder="t('offer-manager.create.step-services.lbl-rate-services-placeholder')"
              :type="InputTypes.PRICE"
              :forceFloat="true"
              :isReadonly="true"
              @validateForm="validateForm"
              :disabled="disabled" />
          </template>
        </m-form-row>

        <m-form-row v-if="servicesProducts.includes(Product.HondaMapit)">
          <template v-slot:custom-label>
            <div class="d-flex align-center justify-start">
              <div>{{ t('offer-manager.create.step-services.lbl-hm') }}</div>
              <div @click="showInfo('honda-mapit')">
                <v-icon dense class="fa-light fa-circle-info ml-1 px-2 click" color="tertiary" />
              </div>
            </div>
          </template>

          <template v-slot:default>
            <v-row>
              <v-col class="col-4">
                <mapit-input
                  :disabled="true"
                  :isReadonly="true"
                  :type="InputTypes.PRICE"
                  :value="servicesHondaMapit.priceBase"
                  @validateForm="validateForm"
                  uid="offerServicesHondaMapitPriceBase" />
              </v-col>
              <v-col class="col-4">
                <discount-field
                  :disabled="true"
                  :isReadonly="true"
                  :rulesCurrency="servicesDiscountRules"
                  :valueCurrency="servicesHondaMapit.discount"
                  @validateForm="validateForm"
                  uid="offerServicesHondaMapitPriceDiscount" />
              </v-col>
              <v-col class="col-4">
                <mapit-input
                  :disabled="true"
                  :isReadonly="true"
                  :type="InputTypes.PRICE"
                  :value="servicesHondaMapit.total"
                  @validateForm="validateForm"
                  uid="offerServicesHondaMapitPrice" />
              </v-col>
            </v-row>
          </template>
        </m-form-row>

        <m-form-row
          :label="t('offer-manager.create.step-services.lbl-type')"
          v-if="servicesProducts.includes(Product.Insurance)">
          <mapit-input
            uid="offerServicesInsurance"
            :value="servicesInsuranceType"
            :placeholder="t('offer-manager.create.step-services.lbl-type-placeholder')"
            :type="InputTypes.SELECT"
            :listItems="insuranceTypeList"
            @changeValue="
              type => updateOffer('setServicesInsuranceType', {type, needsUpdate: !(isEditing && mainLoading)})
            "
            :disabled="disabled" />
        </m-form-row>

        <m-form-row
          :label="t('offer-manager.create.step-services.lbl-driving-licence-date') + '*'"
          v-if="servicesProducts.includes(Product.Insurance)">
          <m-date-picker
            data-cy="offerServicesClientDrivingLicenceDate"
            v-model="drivingLicenceDate"
            :placeholder="dobPlaceholder"
            :rules="drivingLicenceDateRules"
            :min="drivingLicenceDateMin"
            :max="drivingLicenceDateMax"
            :attach="!disableAttach"
            :locale="locale"
            :disabled="disabled" />
        </m-form-row>

        <m-form-row
          :label="t('offer-manager.create.step-services.lbl-financial-services')"
          v-if="servicesProducts.includes(Product.Finance)">
          <div class="d-flex align-center justify-start py-2">
            <v-icon dense class="fa-light fa-circle-info pr-2" color="tertiary" />
            <span>{{ t('offer-manager.create.step-services.lbl-financial-services-info') }}</span>
          </div>
        </m-form-row>

        <m-form-row :label="t('offer-manager.create.step-total-price.lbl-additional-discount')">
          <v-row class="justify-end">
            <v-col class="col-12">
              <discount-field
                :disabled="!servicesPrice || disabled"
                :hideDetails="true"
                :maxCurrency="servicesPrice"
                :placeholder="t('offer-manager.create.step-services.lbl-discount-placeholder')"
                :rulesCurrency="servicesDiscountRules"
                :rulesPercentage="servicesDiscountRulesPercentage"
                :valueCurrency="servicesDiscount"
                :valuePercentage="servicesDiscountPercent"
                @changeValueCurrency="value => updateOffer('setServicesDiscount', value)"
                @changeValuePercentage="value => updateOffer('setServicesDiscountPercent', value)"
                @validateForm="validateForm"
                uid="offerServicesDiscount" />
            </v-col>
          </v-row>
        </m-form-row>

        <m-form-row :label="t('offer-manager.create.step-services.lbl-total-price')" highlighted>
          <v-row>
            <v-col class="col-4">
              <mapit-input
                uid="offerServicesPriceRate"
                :value="servicesPrice"
                :placeholder="t('offer-manager.create.step-services.lbl-rate-services-placeholder')"
                :type="InputTypes.PRICE"
                :forceFloat="true"
                :isReadonly="true"
                @validateForm="validateForm"
                :disabled="disabled" />
            </v-col>
            <v-col class="col-4">
              <discount-field
                :disabled="true"
                :isReadonly="true"
                :rulesCurrency="servicesDiscountRules"
                :valueCurrency="servicesTotalDiscount"
                @validateForm="validateForm"
                uid="offerServicesTotalDiscount" />
            </v-col>
            <v-col class="col-4">
              <mapit-input
                uid="offerServicesTotalPrice"
                :value="servicesTotalPrice.toFixed(2)"
                :placeholder="t('offer-manager.create.step-services.lbl-price-services-placeholder')"
                :type="InputTypes.PRICE"
                :forceFloat="true"
                :isReadonly="true"
                :disabled="disabled" />
            </v-col>
          </v-row>
        </m-form-row>

        <m-form-row>
          <div class="color-red fs-14" v-if="error">
            {{ error }}
          </div>
        </m-form-row>
      </template>
    </expansion-panel>

    <mapit-dialog :dialog="dialog" @close="closeInfo" v-if="variantSelected">
      <template v-slot:header-text>
        <span>{{ t(`honda.registration.services.${variantSelected}.title`) }}</span>
      </template>
      <template v-slot:body>
        <div class="mb-6" v-html="t(`honda.registration.services.${variantSelected}.body`)" />
      </template>
    </mapit-dialog>
  </div>
</template>

<script lang="ts">
import {InsuranceType, Product} from '@muvio/honda-offers-builder/model'
import {computed, defineComponent, inject, Ref, ref, watch} from '@vue/composition-api'
import {DateTime} from 'luxon'

import MapitDialog from '@/components/dialog/MapitDialog.vue'
import DiscountField from '@/components/general/DiscountField.vue'
import ExpansionPanel from '@/components/general/ExpansionPanel.vue'
import MapitInput from '@/components/general/MapitInput.vue'
import ServiceCheckbox from '@/components/general/ServiceCheckbox.vue'
// @ts-ignore
import {
  drivingLicenceDateRules,
  servicesDiscountRules,
  servicesDiscountRulesPercentage,
} from '@/helpers/offer-manager-rules'
import {useI18n} from '@/i18n'
import {InsuranceTypeListItem} from '@/types'
import {InputTypes} from '@/utils/constants'
import {getDatePattern} from '@/utils/date-helper'
import {mapGetters} from '@/utils/map-store'

export default defineComponent({
  name: 'Services',
  components: {
    ExpansionPanel,
    MapitInput,
    ServiceCheckbox,
    MapitDialog,
    DiscountField,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    panelReadonly: {
      type: Boolean,
      default: false,
    },
    panelHideDivider: {
      type: Boolean,
      default: false,
    },
    disableAttach: {
      type: Boolean,
      default: false,
    },
    safePaddingBottom: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, {root, emit}) {
    const {t} = useI18n()

    /* REFS */
    const variantSelected = ref<string | undefined>(undefined)
    const error = ref<string | undefined>(undefined)
    const dialog = ref<boolean | undefined>(undefined)
    const insuranceTypeList = ref<InsuranceTypeListItem[]>(
      Object.values(InsuranceType).map(item => {
        return {
          text: t(`offer-manager.create.step-services.lbl-types-insurances.${item.toLowerCase()}`).toString(),
          value: item,
        }
      }) || [],
    )

    const isEditing = inject('isEditing') as Ref<boolean>
    const mainLoading = inject('mainLoading') as Ref<boolean>

    /* COMPUTED */
    const {
      getBranch: branch,
      getCountry: country,
      getLocale: locale,
      getCanHaveHP: canHaveHP,
      getCanHaveHPGo: canHaveHPGo,
      getServicesProducts: servicesProducts,
      getServicesDiscount: servicesDiscount,
      getServicesDiscountPercent: servicesDiscountPercent,
      getServicesTotalDiscount: servicesTotalDiscount,
      getServicesTotalPrice: servicesTotalPrice,
      getServicesPrice: servicesPrice,
      getServicesInsuranceType: servicesInsuranceType,
      getVehicleModelName: vehicleModelName,
      getVehicleModelSubname: vehicleModelSubname,
      getVehicleModelInsurance: vehicleModelInsurance,
      getSelectedModel: catalogModel,
      getOffer: offer,
      getCapabilities: capabilities,
      getDecollapsedPanels: decollapsedPanels,
      getServicesDiscountError: servicesDiscountError,
      getServicesDiscountPercentError: servicesDiscountPercentError,
    } = mapGetters(root.$store.getters)

    const availableFinancialServicesOption = computed(() => {
      return (
        catalogModel.value &&
        catalogModel.value.finance &&
        Object.keys(catalogModel.value.finance).length > 0 &&
        (capabilities.value?.finance?.supported ?? root.$preferences(branch.value).finance.supported)
      )
    })

    const isHondaPlusSupported = computed(() => {
      return capabilities.value?.hondaPlus?.supported ?? root.$preferences(branch.value).hondaPlus.supported
    })

    const servicesHondaMapit = computed(() => {
      return {
        priceBase: offer.value?.servicesHondaMapitPriceBase,
        discount: offer.value?.servicesHondaMapitPriceDiscount,
        total: offer.value?.servicesHondaMapitPrice,
      }
    })

    const servicesHondaPlusPrice = computed(() => {
      return offer.value?.servicesHondaPlusPrice
    })

    const drivingLicenceDate = computed({
      get: (): string | undefined => root.$store.getters.getClientDrivingLicenceDate,
      set: (value: string | undefined) => {
        root.$store.commit('SET_DRIVING_LICENCE_DATE', {drivingLicenceDate: value, country: country.value})
        root.$nextTick(() => emit('validateForm'))
      },
    })

    const dobPlaceholder = computed(() => {
      return getDatePattern(locale.value)
    })

    const drivingLicenceDateMin = computed(() => {
      return DateTime.local().minus({years: 100}).toFormat('yyyy-MM-dd')
    })

    const drivingLicenceDateMax = computed(() => {
      return DateTime.now().toISODate()
    })

    /* METHODS */
    function validateForm() {
      emit('validateForm')
    }
    function updateOffer(action: string, value: any) {
      root.$store.dispatch(action, value)
      validateForm()
    }
    function closeInfo() {
      variantSelected.value = undefined
      dialog.value = false
    }
    function showInfo(variant: string) {
      if (variant) {
        variantSelected.value = variant
        dialog.value = true
        root.$gtm.trackEvent({category: 'Offers', action: 'OpenModal', label: `InfoService-${variant}`})
      } else {
        closeInfo()
      }
    }
    function validateServiceDiscount(value: string) {
      return (
        !value ||
        !servicesPrice.value ||
        (servicesPrice.value && parseFloat(value) <= parseFloat(servicesPrice.value) && parseFloat(value) >= 0) ||
        'Error'
      )
    }
    function validateDiscounts() {
      if (validateServiceDiscount(servicesDiscount.value) !== 'Error') {
        if (servicesDiscountPercent.value >= 0 && servicesDiscountPercent.value <= 100) {
          error.value = undefined
        } else {
          error.value = t('offer-manager.create.step-vehicle-registration.lbl-error-discount-percentage').toString()
        }
      } else {
        error.value = t('offer-manager.create.step-vehicle-registration.lbl-error-discount').toString()
      }
    }

    /* WATCH */
    watch(servicesPrice, () => {
      validateDiscounts()
    })
    watch(
      servicesDiscountError,
      errorMessage => {
        if (errorMessage) {
          error.value = t('offer-manager.create.step-vehicle-registration.lbl-error-discount').toString()
        } else {
          error.value = undefined
        }
      },
      {immediate: true},
    )
    watch(
      servicesDiscountPercentError,
      errorMessage => {
        if (errorMessage) {
          error.value = t('offer-manager.create.step-vehicle-registration.lbl-error-discount-percentage').toString()
        } else {
          error.value = undefined
        }
      },
      {immediate: true},
    )
    watch(servicesProducts, actual => {
      const isDecollapsedPanel9 = decollapsedPanels.value.includes(9)
      if (actual.includes(Product.Finance) && !isDecollapsedPanel9) root.$store.dispatch('decollapsePanel', 9)
    })

    return {
      t,
      DateTime,
      Product,
      InputTypes,
      drivingLicenceDateRules,
      servicesDiscountRulesPercentage,
      servicesDiscountRules,
      insuranceTypeList,
      variantSelected,
      error,
      dialog,
      branch,
      locale,
      canHaveHP,
      canHaveHPGo,
      servicesProducts,
      servicesDiscount,
      servicesDiscountPercent,
      servicesTotalDiscount,
      servicesDiscountError,
      servicesTotalPrice,
      servicesPrice,
      servicesInsuranceType,
      vehicleModelName,
      vehicleModelSubname,
      vehicleModelInsurance,
      catalogModel,
      availableFinancialServicesOption,
      isHondaPlusSupported,
      servicesHondaMapit,
      servicesHondaPlusPrice,
      drivingLicenceDate,
      drivingLicenceDateMin,
      drivingLicenceDateMax,
      dobPlaceholder,
      isEditing,
      mainLoading,
      updateOffer,
      validateForm,
      closeInfo,
      showInfo,
      validateServiceDiscount,
      validateDiscounts,
    }
  },
})
</script>
<style lang="scss">
.v-expansion-panel:before {
  box-shadow: none !important;
}
.add-rowform {
  color: #0075eb;
  .icon-add:before {
    content: '\e023';
  }
  div:hover {
    opacity: 0.6;
    cursor: pointer;
  }
}
.label-proxima {
  font-family: 'ProximaNovaRegular', sans-serif;
  font-size: 16px !important;
}
.text-title-section {
  color: black;
  font-size: 16px !important;
  letter-spacing: 0.48px;
  line-height: 20px;
  font-family: 'ProximaNovaExCnSbold', sans-serif;
  width: 25%;
}
.text-wrapper {
  display: flex;
  margin-top: 5px;
}
.input-wrapper {
  width: 100%;
}
.data-wrapper {
  width: 100%;
}
.section_wrapper {
  display: flex;
}
.text-description-section {
  font-family: 'ProximaNovaRegular', sans-serif;
  font-size: 16px !important;
  letter-spacing: 0px;
}
.btn-info {
  position: relative;
  font-size: 24px;
  height: 24px;
  width: 24px;
  color: #0075eb;
}
.text-divider {
  border-color: var(--v-gray-lighten2) !important;
  margin-bottom: 10px;
  margin-top: 10px;
}
</style>
